import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";
import {
  editPriceCalculationRequest,
  updatePriceCalculationFormState,
} from "Store/priceCalculation/priceCalculationSlice";
import {
  mapValidationErrorsDitto,
  nullableFormBooleanToNullableBoolean,
} from "Utils/formUtils";

import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { RadioButtonTrueFalse } from "Components/Form/RadioButton";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import React from "react";
import { useDispatch } from "react-redux";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { nextStep } from "Store/steps/stepsSlice";
import { getError } from "Utils/productUtils";
import { FindAddress } from "../../FormComponentsHouseProducts/FindAddress";
import { HouseContentsProductIntroFormValues } from "../houseContentsFormValuesInterfaces";
import { HOUSE_CONTENTS_INTRO_STEP } from "../houseContentsSteps";
import { validationSchema } from "./houseContentsProductIntroValidation";

import { Data } from "Components/TextContext/textInterfaces";

interface Props {
  shortTitleTexts: Data[];
}

export const HouseContentsProductIntro = ({
  shortTitleTexts,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const initialFormState = useFormState<HouseContentsProductIntroFormValues>(
    HOUSE_CONTENTS_INTRO_STEP.id,
    initialFormValues,
  );

  const { getText } = useTexts();

  const handleSubmit = (form: HouseContentsProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: HOUSE_CONTENTS_INTRO_STEP.id,
        formState: {
          ...form,
          fullRental:
            form.addressSameAsHomeAddress === BooleanFormType.TRUE
              ? undefined
              : form.fullRental,
        },
      }),
    );

    dispatch(
      editPriceCalculationRequest({
        addressSameAsHomeAddress:
          nullableFormBooleanToNullableBoolean(form.addressSameAsHomeAddress) ??
          false,
        ...(form.addressSameAsHomeAddress === BooleanFormType.TRUE
          ? { postNumber: undefined }
          : { postNumber: form.postNumber }),
      }),
    );
    dispatch(nextStep());
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.HOUSE_CONTENTS}`,
        );
      }}
    >
      <ProductIntroTile
        productBackendName={ProductBackendName.HOUSE_CONTENTS}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={getText("housecontentsform.pageheader.productintro")}
      >
        {(formProps: FormikProps<HouseContentsProductIntroFormValues>) => {
          return (
            <Form>
              <FindAddress
                formProps={formProps}
                label={getText("housecontentsform.label.postnumber")}
              />

              {formProps.values.addressSameAsHomeAddress ===
                BooleanFormType.FALSE && (
                <RadioButtonTrueFalse
                  name="fullRental"
                  id="fullRental"
                  label={getText("housecontentsform.label.fullrental")}
                  error={getError("fullRental", formProps)}
                />
              )}

              <PartnerProductIntroForms />
              <MarketingConsent />

              {formProps.submitCount > 0 &&
                Object.keys(formProps.errors).length > 0 && (
                  <ErrorsSummedUp
                    errors={mapValidationErrorsDitto(
                      formProps.errors,
                      shortTitleTexts,
                    )}
                    isVisible
                  />
                )}

              <ScrollToFirstError />

              <NavigationButtons hidePreviousButton />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.HOUSE_CONTENTS}
                stepId={HOUSE_CONTENTS_INTRO_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues = {
  addressSameAsHomeAddress: undefined,
  postNumber: undefined,
  city: undefined,
  validPostNumber: undefined,
  fullRental: undefined,
};
